/// <amd-module name="Reporting/BoardsReportView/VelocityReportCard" />
import { AggregationMode, Metastate, ModefulValueSetting } from "Analytics/Boards/BoardsQueries/DataContracts";
import { Iteration, IterationsQuery } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { ProcessesQuery } from "Analytics/Boards/BoardsQueries/ProcessesQuery";
import { MetastateByIterationsQuery } from "Analytics/Boards/VelocityQueries/MetastateByIterationsQuery";
import { AnalyticsExceptionType, extractODataError, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { CardMetricsVisual, ICardMetricsProps, MetricSize } from "Analytics/Shared/Visuals/CardMetricsVisual/CardMetricsVisual";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import * as React from "react";
import { publishEmptyReportTelemetry, telemetryAreaName } from "Reporting/BoardsCommon/ReportTelemetry";
import * as Resources from "Reporting/BoardsReportView/Resources";
import { ICacheableQuery } from "Reporting/Common/ICacheableQuery";
import { VelocityReportUserSettings } from "Reporting/VelocityReport/VelocityReportUserSettings";
import { ICancelablePromise } from "VSS/Core/Util/Promise";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { traceException } from "VSS/Platform/Trace";
import { VelocityDataHelper } from "Widgets/VelocityWidget/VelocityWidgetComponents/VelocityDataHelper";
import { BoardsReportCardProps } from "./BoardsReportSelectorProps";

interface VelocityReportCardState {
    velocityValue?: number;
    MessageType?: MessageType;
    errorMessage?: string;
}

interface AverageVelocityDataResult {
    messageType: MessageType;
    message?: string;
    value?: number;
}

enum MessageType {
    SetIterationDates,
    AxFaultIn,
    DataError,
    Success
}

/**
 * Retrieves average velocity data and renders a velocity card with a hero metric.
 */
export class VelocityReportCard extends VssComponent<BoardsReportCardProps, VelocityReportCardState> {
    private static readonly ReportTelemetryFeatureName: string = "VelocityReportCard";
    private queryService: ICacheableQueryService;
    private velocityDataPromise: ICancelablePromise<AverageVelocityDataResult>;

    constructor(props: BoardsReportCardProps, context?: IVssComponentContext) {
        super(props, context);
        this.queryService = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        this.state = {
            velocityValue: undefined
        };
    }

    public async componentDidMount() {
        this.loadVelocityMetricsData();
    }

    public async componentDidUpdate(prevProps: BoardsReportCardProps) {
        if (JSON.stringify(prevProps) !== JSON.stringify(this.props)) {
            this.loadVelocityMetricsData();
        }
    }

    public render(): JSX.Element {
        const cardMetricsProps = this.getCardMetricsProps();
        return <CardMetricsVisual {...cardMetricsProps} />;
    }

    private async loadVelocityMetricsData() {
        this.velocityDataPromise && this.velocityDataPromise.cancel();

        try {
            this.velocityDataPromise = this.trackPromise(this.getAverageVelocity());

            const velocityData = await this.velocityDataPromise.promise;

            this.setState({
                MessageType: velocityData.messageType,
                velocityValue: velocityData.value,
                errorMessage: velocityData.message
            });
        } catch (error) {
            if (error.isCanceled) {
                return; // Do nothing if the promises were cancelled
            }

            traceException(this.context.pageContext, telemetryAreaName, VelocityReportCard.ReportTelemetryFeatureName, error);
            this.setState({
                errorMessage: error.message
            });
        }
    }

    private getCardMetricsProps(): ICardMetricsProps {
        let secondaryMetric = undefined;
        let errorText = "";
        let onCardClick = this.props.onCardClick;
        if (this.state.velocityValue !== undefined) {
            secondaryMetric = {
                label: Resources.VelocityMetricLabel,
                value: this.state.velocityValue.toLocaleString()
            };
        }
        if (this.state.MessageType !== undefined && this.state.MessageType !== MessageType.Success) {
            switch (this.state.MessageType) {
                case MessageType.SetIterationDates: {
                    errorText = Resources.VelocityCardSetIterationDatesMessage;
                    onCardClick = this.navigateToSetIterationsPage;
                    break;
                }
                case MessageType.AxFaultIn: {
                    errorText = Resources.AxFaultInMessage;
                    break;
                }
                case MessageType.DataError: {
                    // Show whatever we can
                    errorText = this.state.errorMessage || Resources.VelocityUnknownErrorMessageText;
                    break;
                }
                default: {
                    break;
                }
            }
        }

        return {
            title: Resources.VelocityReportName,
            dataValidationPromises: [],
            primaryMetric: undefined,
            secondaryMetric,
            secondaryMetricSize: {
                label: MetricSize.Medium,
                value: MetricSize.XLarge
            },
            insight: "",
            onCardClick,
            errorText
        };
    }

    private navigateToSetIterationsPage = () => {
        const linkToSetIterations = routeUrl(this.context.pageContext, "ms.vss-admin-web.project-admin-hub-route", {
            project: this.props.projectId,
            adminPivot: "work"
        });
        FastPageSwitch(this.context.pageContext, linkToSetIterations, true);
    };

    private async getAverageVelocity(): Promise<AverageVelocityDataResult> {
        const userSettings = await VelocityReportUserSettings.load(this.context.pageContext, this.props.teamId, this.props.categoryReferenceName);
        try {
            const [iterations, workItemTypes] = await Promise.all([
                this.getIterations(this.props.projectId, this.props.teamId, userSettings.numberOfIterations || 6),
                this.getWorkItemTypes(this.props.projectId, this.props.teamId, this.props.categoryReferenceName)
            ]);

            if (iterations.length === 0) {
                // No iteration data available - set iteration dates.
                publishEmptyReportTelemetry(
                    this.context.pageContext,
                    VelocityReportCard.ReportTelemetryFeatureName,
                    Resources.VelocityCardSetIterationDatesMessage
                );
                return {
                    messageType: MessageType.SetIterationDates,
                    message: Resources.VelocityCardSetIterationDatesMessage
                };
            }

            const iterationIds: string[] = [];
            iterations
                .filter(x => x.IterationSK)
                .map((iteration: Iteration) => {
                    iterationIds.push(iteration.IterationSK);
                });
            const aggregationSettings = userSettings.aggregationSettings
                ? userSettings.aggregationSettings
                : {
                      identifier: AggregationMode.Count,
                      settings: "" // empty settings can be passed in only with aggregation mode count
                  };
            const metastateQueryResult = await this.getMetastate(
                iterationIds,
                this.props.projectId,
                this.props.teamId,
                aggregationSettings,
                workItemTypes
            );
            return {
                messageType: MessageType.Success,
                value: VelocityDataHelper.getAverageVelocity(metastateQueryResult, iterations)
            };
        } catch (error) {
            // Catch pattern
            let messageType = MessageType.DataError;
            const errorMessage = extractODataError(error);
            // Check if we should show fault-in instead
            if (recognizeAnalyticsException(error) === AnalyticsExceptionType.DataNotReady) {
                messageType = MessageType.AxFaultIn;
                publishEmptyReportTelemetry(this.context.pageContext, VelocityReportCard.ReportTelemetryFeatureName, errorMessage);
            } else {
                traceException(this.context.pageContext, telemetryAreaName, VelocityReportCard.ReportTelemetryFeatureName, error);
            }
            return {
                messageType,
                message: errorMessage
            };
        }
    }

    private async getWorkItemTypes(projectId: string, teamId: string, backlogCategory: string): Promise<string[]> {
        const processesQuery = ProcessesQuery.onTeam(this.context.pageContext, projectId, teamId);
        const workItemTypes = await this.runQuery(processesQuery);
        return workItemTypes
            .map(entry => {
                return entry.BacklogCategoryReferenceName === backlogCategory ? entry.WorkItemType : "";
            })
            .filter(o => o != "");
    }

    private getIterations(projectId: string, teamId: string, desiredIterationCount: number): Promise<Iteration[]> {
        const iterationsQuery = IterationsQuery.onTeam(projectId, teamId, desiredIterationCount);
        return this.runQuery<Iteration[]>(iterationsQuery);
    }

    private getMetastate(
        iterationIds: string[],
        projectId: string,
        teamId: string,
        aggregation: ModefulValueSetting<AggregationMode, string>,
        workItemTypes: string[]
    ): Promise<Metastate[]> {
        const metastateQuery = new MetastateByIterationsQuery("VelocityReport", projectId, teamId, iterationIds, aggregation, workItemTypes);
        return this.runQuery<Metastate[]>(metastateQuery);
    }

    private async runQuery<T>(query: ICacheableQuery<T>): Promise<T> {
        return this.queryService.getCacheableQueryResult(query);
    }
}
